import { useMutation, useQuery, useQueryClient } from "react-query";
import qs from "qs-lite";

import api from "../../utils/api";
import { useHubSnackBar } from "../useSnackBar";
import { CUSTOMERS } from "./useCustomerData";
import { invalidate } from "../../helpers/clientHelper";

export const ORDERS = "/order";

const postOrderData = (data) => {
  const isVendor = data?.isVendor;
  delete data?.isVendor;

  const URL = isVendor ? `${ORDERS}/vendor` : ORDERS;

  return api.post(URL, data);
}
export const usePostOrderData = () => {
  const { addSnackbarSuccess } = useHubSnackBar();
  const queryClient = useQueryClient();

  return useMutation(postOrderData, {
    onSuccess: () => {
      invalidate(queryClient, CUSTOMERS);
      invalidate(queryClient, ORDERS);
      addSnackbarSuccess({ message: "Successfully Created Purchase Order" });
    },
  });
};

const putOrderData = (data) => {
  const id = data.id;
  delete data.id;
  return api.put(`${ORDERS}/${id}`, data);
};
const putVendorOrderData = (data) => {
  const id = data.id;
  delete data.id;
  return api.put(`${ORDERS}/vendor/${id}`, data);
};
export const usePutOrderData = (isVendor) => {
  const { addSnackbarSuccess } = useHubSnackBar();
  const queryClient = useQueryClient();

  const mutation = isVendor ? putVendorOrderData : putOrderData;
  return useMutation(mutation, {
    onSuccess: () => {
      invalidate(queryClient, ORDERS);
      addSnackbarSuccess({ message: "Successfully Updated Purchase Order" });
    },
  });
};

const putShipVendorOrderData = (data) => {
  const id = data.id;
  delete data.id;
  return api.put(`${ORDERS}/shipment/${id}`, data);
};
export const usePutShipOrderData = () => {
  const { addSnackbarSuccess } = useHubSnackBar();
  const queryClient = useQueryClient();

  return useMutation(putShipVendorOrderData, {
    onSuccess: () => {
      invalidate(queryClient, ORDERS);
      addSnackbarSuccess({ message: "Successfully Updated Purchase Order" });
    },
  });
};

const getAllOrderData = ({ queryKey }) => {
  const queryString = qs.stringify(queryKey[1]);
  return api.get(`${ORDERS}?${queryString}`);
};

export const useGetAllOrderData = ({ per, start, end, defaultLocation }) => {
  const query = new URLSearchParams(window.location.search);

  const queryLocation = query.get("location");
  const location =
    queryLocation === "All" ? "" : queryLocation || defaultLocation;
  const status = query.get("status") || "Not Started-In Progress";

  const orderBy = query.get("orderBy") || "createdAt";
  const sortOrder = query.get("sortOrder") || "desc";
  const limit = query.get("limit") || 100;
  const offset = query.get("offset") || 0;

  const period = query.get("period") || per;
  const startDate = query.get("startDate") || start;
  const endDate = query.get("endDate") || end;

  const queryProps = {
    ...(location ? { location } : {}),
    ...(status !== "All" && status ? { status } : {}),
    limit,
    offset,
    orderBy,
    sortOrder,
    period,
    startDate,
    endDate,
  };

  return useQuery([ORDERS, { ...queryProps }], getAllOrderData, {
    enabled: !!defaultLocation,
  });
};

const getOutboundOrders = ({ queryKey }) => {
  const queryString = qs.stringify(queryKey[1]);
  return api.get(`${ORDERS}/outbound?${queryString}`);
};

export const useGetOutboundOrders = ({ defaultLocation }) => {
  const query = new URLSearchParams(window.location.search);

  const queryLocation = query.get("location");
  const locationId = queryLocation || defaultLocation;

  const orderBy = query.get("orderBy") || "createdAt";
  const sortOrder = query.get("sortOrder") || "desc";
  const limit = query.get("limit") || 100;
  const offset = query.get("offset") || 0;

  const isComplete = query.get("isComplete") || false;
  const isShipment = query.get("isShipment") || true;

  const queryProps = {
    ...(locationId ? { locationId } : {}),
    limit,
    offset,
    orderBy,
    sortOrder,
    isComplete,
    isShipment,
  };

  return useQuery([ORDERS, { ...queryProps }, "outbound"], getOutboundOrders, {
    enabled: !!defaultLocation,
  });
};

const deleteOrderById = (id) => api.delete(`${ORDERS}/${id}`);
export const useDeleteOrderById = () => {
  const { addSnackbarSuccess } = useHubSnackBar();
  const queryClient = useQueryClient();

  return useMutation(deleteOrderById, {
    onSuccess: () => {
      queryClient.invalidateQueries(ORDERS);
      queryClient.invalidateQueries(CUSTOMERS);
      addSnackbarSuccess({ message: "Successfully Deleted Order" });
    },
  });
};

const getOrderEditData = (id) => api.get(`${ORDERS}/edit/${id}`);
export const useGetOrderEditData = (id) => {
  return useQuery([ORDERS, id], () => getOrderEditData(id), {
    enabled: !!id,
  });
};

const getOutboundShipData = ({ queryKey }) => {
  // eslint-disable-next-line
  const [_, id] = queryKey;
  return api.get(`${ORDERS}/shipment/${id}`);
};
export const useGetOutboundShipData = (id) => {
  return useQuery([ORDERS, id, "/shipment"], getOutboundShipData, {
    enabled: !!id,
  });
};

const getOrderLineItems = (id) => api.get(`${ORDERS}/lines/${id}`);
export const useGetOrderLineItems = (id, open) => {
  return useQuery([`${ORDERS}/lines/${id}`], () => getOrderLineItems(id), {
    enabled: !isNaN(id) && open,
  });
};

const getOrderLineWorkOrders = ({ queryKey }) => {
  const id = queryKey[1];
  const queryString = qs.stringify(queryKey[2]);
  return api.get(`${ORDERS}/lines/${id}/works?${queryString}`);
};
export const useGetOrderLineWorkOrders = (id, open) => {
  const query = new URLSearchParams(window.location.search);
  const enabled = !isNaN(Number(id)) && open;

  const orderBy = query.get("orderBy") || "startDate";
  const sortOrder = query.get("sortOrder") || "desc";
  const limit = query.get("limit") || 10;
  const offset = query.get("offset") || 0;

  const queryProps = {
    limit,
    offset,
    orderBy,
    sortOrder,
  };

  return useQuery(
    [`${ORDERS}/lines/works`, id, queryProps],
    getOrderLineWorkOrders,
    { enabled }
  );
};

const getOrderLineItemDetails = (id) =>
  api.get(`${ORDERS}/lines/details/${id}`);
export const useGetOrderLineItemDetails = (id) => {
  return useQuery(
    [`${ORDERS}/lines/details/${id}`],
    () => getOrderLineItemDetails(id),
    {
      enabled: !isNaN(id),
    }
  );
};

const getCustomerOrderData = ({ queryKey }) => {
  const [, id, query] = queryKey;
  const queryString = qs.stringify(query);
  return api.get(`${ORDERS}/customer/${id}?${queryString}`);
};
export const useGetCustomerOrderData = (id) => {
  const query = new URLSearchParams(window.location.search);

  const orderBy = query.get("orderBy") || "createdAt";
  const sortOrder = query.get("sortOrder") || "desc";
  const limit = query.get("limit") || 25;
  const offset = query.get("offset") || 0;

  const queryProps = {
    limit,
    offset,
    orderBy,
    sortOrder,
  };

  return useQuery(
    [`${ORDERS}/customer`, id, { ...queryProps }],
    getCustomerOrderData
  );
};

const getOrdersByPartData = ({ queryKey }) => {
  const queryString = qs.stringify(queryKey[1]);
  const id = Number(queryKey[2]);
  return api.get(`${ORDERS}/part/${id}?${queryString}`);
};

export const useGetOrdersByPartData = (id) => {
  const query = new URLSearchParams(window.location.search);

  const orderBy = query.get("orderBy") || "createdAt";
  const sortOrder = query.get("sortOrder") || "desc";
  const limit = query.get("limit") || 100;
  const offset = query.get("offset") || 0;

  const queryProps = {
    limit,
    offset,
    orderBy,
    sortOrder,
  };

  return useQuery([ORDERS, { ...queryProps }, id], getOrdersByPartData);
};
